import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ConfigInformedConsents.scss';
import { useApiReports } from '../../api/api.reports';
import { useApiInformedConsents } from '../../api/api.informedConsents';
import DataGrid, { Column, Button as Butt, Editing, Item, Scrolling, Sorting, Toolbar, SearchPanel, Lookup, Selection, Form as FormGrid } from 'devextreme-react/data-grid';
import Form, { Item as FormItem } from 'devextreme-react/form';
import Popup, { Position } from 'devextreme-react/popup';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import Button from 'devextreme-react/button';
import { Tooltip } from 'devextreme-react';
import { useAppSelector } from '../../contexts/hooks';

const Index = () => {
  const [dataReports, setDataReports] = React.useState<any[]>([]);
  const { GetInformedConsentsList } = useApiReports();
  const { getConsents, createConsent, updateConsent, deleteConsent } = useApiInformedConsents();
  const dataGridRef = useRef<DataGrid>(null);
  const dataStudies = useAppSelector((x) => x.configuration.Data?.Estudios);
  const [isConfigModalVisible, setIsConfigModalVisible] = useState(false);
  const [configTemplate, setConfigTemplate] = useState<any[]>([]);
  const [editingField, setEditingField] = useState<any | null>(null);
  const [currentConsent, setCurrentConsent] = useState<any | null>(null);

  useEffect(() => {
    // Cargar datos de reportes al iniciar
    GetInformedConsentsList().then((res) => {
      setDataReports(res);
    });
  }, []);

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'Cst_Id',
      load() {
        return getConsents()
          .then((res) => ({
            data: res,
            totalCount: res.totalCount,
            summary: res.summary,
            groupCount: res.groupCount,
          }))
          .catch(() => {
            throw new Error('Error de carga de datos');
          });
      },
      insert: async (values) => {
        return createConsent(values)
          .then((res) => {
            if (res === 105) {
              dataGridRef.current?.instance.refresh();
            }
          })
          .catch(() => {
            throw new Error('Error insertando el analito');
          });
      },
      update: (key, values) => {
        //Obtenemos el objeto original para evitar perder datos no editados de la data del grid y no de datareports
        const originalData = dataGridRef.current?.instance
          .getDataSource()
          .items()
          .find((item: any) => item.Cst_Id === key);
        const updatedData = { ...originalData, ...values };
        return updateConsent(updatedData)
          .then((res) => {
            if (res === 107) {
              dataGridRef.current?.instance.refresh();
            }
          })
          .catch(() => {
            throw new Error('Error actualizando el analito');
          });
      },
      remove: (key) => {
        return deleteConsent(key).then((res) => {
          dataGridRef.current?.instance.refresh();
        });
      },
    });
  }, []);

  const customDataSrc = new DataSource({
    store: customData,
    reshapeOnPush: true,
  });

  const handleUpdateData = useCallback(() => {
    dataGridRef.current?.instance.refresh();
  }, []);

  const handleConfigureClick = useCallback((e: any) => {
    const data = e?.row?.data || e?.data || null;
    // Abrir modal de configuración cargando la plantilla existente (JSON)
    let template: any[] = [];
    try {
      if (data?.Cst_Plantilla) {
        template = typeof data.Cst_Plantilla === 'string' ? JSON.parse(data.Cst_Plantilla) : data.Cst_Plantilla;
      }
    } catch (err) {
      console.error('Error parsing Cst_Plantilla JSON', err);
      template = [];
    }
  setConfigTemplate(template || []);
    setEditingField(null);
    setCurrentConsent(data);
    setIsConfigModalVisible(true);
  }, [setConfigTemplate, setIsConfigModalVisible, setCurrentConsent]);

  const addField = () => {
    const newField = {
      id: Date.now(),
      key: `f_${Date.now()}`,
      label: 'Nuevo campo',
      editorType: 'dxTextBox',
      required: false,
      defaultValue: '',
      lookupValues: '',
    };
    setConfigTemplate((s) => [...s, newField]);
    setEditingField(newField);
  };

  const deleteField = (id: number) => {
    setConfigTemplate((s) => s.filter((f) => f.id !== id));
    setEditingField(null);
  };

  const saveField = (field: any) => {
    setConfigTemplate((s) => {
      const exists = s.find((f) => f.id === field.id);
      if (exists) {
        return s.map((f) => (f.id === field.id ? field : f));
      }
      return [...s, field];
    });
    setEditingField(null);
  };

  const moveField = (id: number, direction: 'up' | 'down') => {
    setConfigTemplate((s) => {
      const idx = s.findIndex((f) => f.id === id);
      if (idx === -1) return s;
      const n = [...s];
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= n.length) return s;
      const tmp = n[swapIdx];
      n[swapIdx] = n[idx];
      n[idx] = tmp;
      return n;
    });
  };

  const saveTemplate = async () => {
    if (!currentConsent) return;
    const updated = { ...currentConsent, Cst_Plantilla: JSON.stringify(configTemplate) };
    try {
      await updateConsent(updated);
      dataGridRef.current?.instance.refresh();
      setIsConfigModalVisible(false);
    } catch (err) {
      console.error('Error saving plantilla', err);
    }
  };

  return (
    <div className='view crm-contact-list'>
      <div className='view-wrapper view-wrapper-contact-list'>
        <DataGrid
          className='grid'
          ref={dataGridRef}
          dataSource={customDataSrc}
          width={'100%'}
          hoverStateEnabled={true}
          height='100vh'
          repaintChangesOnly={true}
          highlightChanges={true}
          rowAlternationEnabled={true}
          showColumnLines={true}          
          onInitNewRow={(e) => {
            e.data.Cst_Estado = true;
          }}>
          <SearchPanel visible={true} />
          <Scrolling columnRenderingMode='standard' mode='virtual' useNative={true} scrollByContent={true} showScrollbar='always' />
          <Sorting mode='multiple' />
          <Selection mode='single' />
          <Editing mode='popup' allowUpdating={true} allowAdding={true} allowDeleting={true} selectTextOnEditStart={true} texts={{ saveRowChanges: 'Guardar', cancelRowChanges: 'Cancelar', deleteRow: 'Eliminar', addRow: 'Agregar' }}>
            <Popup title='INFORMACIÓN ANALITO' showTitle={true}>
              <Position my='center' at='center' of={window} />
            </Popup>
            <FormGrid colCount={2}>
              <FormItem dataField='Cst_Id' caption='ID' editorOptions={{ readOnly: true }} />
              <FormItem dataField='Cst_Rp_Id' editorType='dxLookup' editorOptions={{ dataSource: dataReports, valueExpr: 'Rp_Id', displayExpr: 'Rp_Nombre' }} />
              <FormItem dataField='Cst_Nombre' />
              <FormItem dataField='Cst_Es_Id' editorType='dxLookup' editorOptions={{ dataSource: dataStudies, valueExpr: 'Es_Id', displayExpr: (item: any) => (item ? `${item.Es_Codigo} - ${item.Es_Nombre}` : '') }} />
              <FormItem dataField='Cst_Descripcion' editorType='dxTextArea' colSpan={2} editorOptions={{ height: 120 }} />
              <FormItem dataField='Cst_Estado' editorType='dxCheckBox' />
            </FormGrid>
          </Editing>
          <Toolbar>
            <Item location='before'>
              <div className='grid-header'>Consentimientos informados</div>
            </Item>
            <Item name='searchPanel' />
            <Item name='addRowButton' />
            <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button id='RefrecarResult' icon='refresh' text='Refresh' stylingMode='text' onClick={handleUpdateData} />
              <Tooltip target={'#RefrecarResult'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                Refrescar datos
              </Tooltip>
            </Item>
          </Toolbar>
          <Column dataField='Cst_Id' caption='ID' allowEditing={false} width={70} alignment='center' />
          <Column dataField='Cst_Rp_Id' caption='REPORTE' width={250}>
            <Lookup dataSource={dataReports} valueExpr='Rp_Id' displayExpr='Rp_Nombre' />
          </Column>
          <Column dataField='Cst_Nombre' caption='NOMBRE' width={200} />
          <Column dataField='Cst_Es_Id' caption='ESTUDIO'>
            <Lookup dataSource={dataStudies} valueExpr='Es_Id' displayExpr={(item: any) => (item ? `${item.Es_Codigo} - ${item.Es_Nombre}` : '')} />
          </Column>
          <Column dataField='Cst_Plantilla' visible={false} caption='PLANTILLA' width={250} />
          <Column dataField='Cst_Descripcion' caption='DESCRIPCIÓN' visible={false} width={250} />
          <Column dataField='Cst_Estado' caption='ESTADO' width={80} />
          <Column type='buttons' caption={'ACCIÓN'} width={100}>
            <Butt name='edit' />
            <Butt name='delete' />
            <Butt hint='Configurar' icon='preferences' visible={true} disabled={false} onClick={handleConfigureClick} />
          </Column>
        </DataGrid>
        
        <Popup
          visible={isConfigModalVisible}
          title='Configurar plantilla'
          showTitle={true}
          showCloseButton={true}
          hideOnOutsideClick={true}
          width={800}
          height={600}
          onHiding={() => setIsConfigModalVisible(false)}>
          <div style={{ display: 'flex', gap: 16, padding: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <div>
                  <Button text='Agregar campo' icon='plus' stylingMode='contained' onClick={addField} />
                </div>
                <div>
                  <Button text='Guardar plantilla' icon='save' stylingMode='contained' onClick={saveTemplate} />
                  <Button text='Cerrar' stylingMode='text' onClick={() => setIsConfigModalVisible(false)} />
                </div>
              </div>
              <DataGrid dataSource={configTemplate} showBorders={true} height={360} keyExpr='id' onRowClick={(e) => setEditingField(e.data)}>
                <Scrolling columnRenderingMode='standard' />
                <Selection mode='single' />
                {/* Mostrar solo el nombre del campo y las acciones */}
                <Column dataField='label' caption='Campo' />
                <Column type='buttons' width={140}>
                  <Butt icon='edit' hint='Editar' onClick={(e: any) => setEditingField(e.row.data)} />
                  <Butt icon='trash' hint='Eliminar' onClick={(e: any) => deleteField(e.row.data.id)} />
                  <Butt icon='chevronup' hint='Mover arriba' onClick={(e: any) => moveField(e.row.data.id, 'up')} />
                  <Butt icon='chevrondown' hint='Mover abajo' onClick={(e: any) => moveField(e.row.data.id, 'down')} />
                </Column>
              </DataGrid>
            </div>
            <div style={{ width: 360 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Editar campo</div>
              <Form formData={editingField || {}} onFieldDataChanged={(e) => {
                if (!editingField) return;
                const key = e.dataField as string;
                const updated = { ...editingField, [key]: e.value };
                setEditingField(updated);
              }}>
                <FormItem dataField='label' editorType='dxTextBox' editorOptions={{ disabled: !editingField }} />
                <FormItem dataField='key' editorType='dxTextBox' editorOptions={{ disabled: !editingField }} />
                <FormItem dataField='editorType' editorType='dxSelectBox' editorOptions={{ dataSource: ['dxTextBox', 'dxTextArea', 'dxLookup', 'dxCheckBox', 'dxDateBox'], disabled: !editingField }} />
                <FormItem dataField='required' editorType='dxCheckBox' editorOptions={{ disabled: !editingField }} />
                <FormItem dataField='defaultValue' editorType='dxTextBox' editorOptions={{ disabled: !editingField }} />
                <FormItem dataField='lookupValues' editorType='dxTextArea' editorOptions={{ height: 80, disabled: !editingField }} helpText='Valores separados por coma para lookup/select' />
              </Form>
              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                <Button text='Guardar campo' onClick={() => editingField && saveField(editingField)} disabled={!editingField} />
                <Button text='Cancelar' stylingMode='text' onClick={() => setEditingField(null)} />
              </div>
            </div>
          </div>
        </Popup>
      </div>
    </div>
  );
};

export default Index;
